import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useBlocker } from "react-router";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FormStepper } from "sj-ui-toolkit/src/components";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts";
import { getPlaClient, type TenantPLAResponse } from "sj-ui-toolkit/src/client/svcClients";
import CreatePlaDialog from "@/components/pla/CreatePlaDialog";
import TrainingAssignmentSettings, {
  type TrainingAssignmentSettingsValues,
} from "@/components/pla/TrainingAssignmentSettings";
import TrainingAvailableAssignments from "@/components/pla/TrainingAvailableAssignments";
import TrainingReview from "@/components/pla/TrainingReview";
import type { TrainingPlaAssignment } from "@/components/pla/types";
import { useToast } from "sj-ui-toolkit/src/hooks";

const TOTAL_STEPS = 3;
const DEFAULT_NAME = "Personalized Assignment for {User Name} — {Month Year}";
const DEFAULT_DESCRIPTION =
  "This assignment was selected for you based on your recent code activity. Completing it will help strengthen your security practices.";

const normalizeToMidnight = (date: string | Date) => {
  const normalizedDate = new Date(date);
  normalizedDate.setHours(0, 0, 0, 0);
  return normalizedDate;
};

const getDefaultSettings = (): TrainingAssignmentSettingsValues => ({
  name: DEFAULT_NAME,
  description: DEFAULT_DESCRIPTION,
  startDate: null,
  endDate: null,
  reminders: "none",
});

const getAssignmentsValidation = (selections: TrainingPlaAssignment[]) => ({
  hasError: selections.length === 0,
  message: selections.length === 0 ? "Please select at least one assignment" : "",
});

const getStartDateError = ({
  startDate,
  endDate,
}: Pick<TrainingAssignmentSettingsValues, "startDate" | "endDate">) => {
  if (!startDate) return "Please select a start date";

  const start = normalizeToMidnight(startDate);
  const today = normalizeToMidnight(new Date());
  if (start < today) return "Start date can't be in the past";
  if (endDate && start >= new Date(endDate)) {
    return "Start date must be before the end date";
  }

  return "";
};

const getSettingsValidation = (settings: TrainingAssignmentSettingsValues) => {
  const startDateError = getStartDateError(settings);
  return {
    description: {
      hasError: !settings.description?.trim(),
      message: !settings.description?.trim() ? "Description is required" : "",
    },
    startDate: {
      hasError: Boolean(startDateError),
      message: startDateError,
    },
    timeframe: {
      hasError: !settings.endDate,
      message: !settings.endDate ? "Please select a timeframe" : "",
    },
  };
};

const getInitialValidationErrors = () => ({
  assignments: getAssignmentsValidation([]),
  settings: getSettingsValidation(getDefaultSettings()),
});

type PlaAssignment = NonNullable<TenantPLAResponse["assignments"]>[number];

const transformPlaData = (plas: PlaAssignment[] = []) => {
  const activePlaCounts: Record<string, number> = {};
  plas.forEach((pla: PlaAssignment) => {
    const learner = (pla.learner ?? {}) as {
      directoryUuid?: string;
      givenName?: string;
      familyName?: string;
      email?: string;
      attributes?: Record<string, unknown>;
    };

    if (!pla.archivedAt) {
      const key = learner.directoryUuid || pla.directoryUuid;
      if (key) {
        activePlaCounts[key] = (activePlaCounts[key] || 0) + 1;
      }
    }
  });

  return plas
    .filter((pla: PlaAssignment) => {
      if (pla.archivedAt || pla.approvedAt) return false;
      // Don't render rows with nothing to assign — no lessons and no recommendations.
      const hasLessons = (pla.lessons?.length || 0) > 0;
      const sources = (pla.sources ?? []) as { lessonsRecommended?: (string | undefined)[] }[];
      const hasRecommendations = sources.some(
        source => (source.lessonsRecommended?.length || 0) > 0
      );
      return hasLessons || hasRecommendations;
    })
    .map((pla: PlaAssignment) => {
      const learner = (pla.learner ?? {}) as {
        directoryUuid?: string;
        givenName?: string;
        familyName?: string;
        email?: string;
        attributes?: Record<string, unknown>;
      };
      const sources = (pla.sources ?? []) as {
        createdAt?: string;
        lessonsRecommended?: (string | undefined)[];
      }[];
      const lastCwe =
        sources.length > 0
          ? sources.reduce<string | null>((latest, source) => {
              if (!source.createdAt) return latest;
              return !latest || source.createdAt > latest ? source.createdAt : latest;
            }, null)
          : null;

      const recommendedKeys = new Set<string>();
      sources.forEach(source => {
        (source.lessonsRecommended || []).forEach((key: string | undefined) => {
          if (key) recommendedKeys.add(key);
        });
      });

      // pla.lessons is the authoritative lesson list. Sources can be empty (e.g.
      // manually created PLAs), so the count and selection must come from it, not
      // from lessonsRecommended — otherwise those PLAs render "0 of 0".
      const allLessonKeys = (pla.lessons || [])
        .map(lesson => lesson.lessonKey)
        .filter((key): key is string => !!key);
      const recommendedInLessons = allLessonKeys.filter(key => recommendedKeys.has(key));
      // Pre-select the recommended lessons; when there are none, select all.
      const defaultSelectedLessonKeys =
        recommendedInLessons.length > 0 ? recommendedInLessons : allLessonKeys;

      const directoryUuid = learner.directoryUuid || pla.directoryUuid || "";
      const activePlaCount = activePlaCounts[directoryUuid] || 0;

      return {
        uuid: pla.uuid || "",
        ...(directoryUuid ? { directoryUuid } : {}),
        displayName: `${learner.givenName || ""} ${learner.familyName || ""}`.trim(),
        givenName: learner.givenName || "",
        familyName: learner.familyName || "",
        email: learner.email || "",
        lessons: pla.lessons || [],
        defaultSelectedLessonKeys,
        lessonsTotal: allLessonKeys.length,
        activePlaCount,
        activePlaDisplay: activePlaCount === 0 ? "None" : `${activePlaCount} Active`,
        lastCweDetection: lastCwe,
        archivedAt: pla.archivedAt || null,
        attributes: (learner.attributes as Record<string, unknown>) || {},
        _original: pla,
      } satisfies TrainingPlaAssignment;
    });
};

interface AspenAdaptTrainingProps {
  tenant: string;
  cweDescriptions: Record<string, { Name?: string; Description?: string }> | null;
}

export default function AspenAdaptTraining({
  tenant,
  cweDescriptions = null,
}: AspenAdaptTrainingProps) {
  const { createPlaData } = useFlags();
  const { isSjAdmin } = useUserContext();
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [showStepStatus, setShowStepStatus] = useState(false);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [assignments, setAssignments] = useState<TrainingPlaAssignment[]>([]);
  const [selectedAssignments, setSelectedAssignments] = useState<TrainingPlaAssignment[]>([]);
  const [lessonSelections, setLessonSelections] = useState<Record<string, string[]>>({});
  const [hasEditedLessons, setHasEditedLessons] = useState(false);
  const [settings, setSettings] = useState<TrainingAssignmentSettingsValues>(getDefaultSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createPlaOpen, setCreatePlaOpen] = useState(false);
  const selectedAssignmentsRef = useRef(selectedAssignments);
  const [validationErrors, setValidationErrors] = useState(getInitialValidationErrors);

  useEffect(() => {
    selectedAssignmentsRef.current = selectedAssignments;
  }, [selectedAssignments]);

  const fetchPlas = useCallback(async () => {
    if (!tenant) return;

    setIsLoading(true);
    try {
      const { data, error } = await getPlaClient().getTenantPLAs(tenant);
      if (error) throw error;
      setAssignments(transformPlaData(data?.assignments));
    } catch (error) {
      console.error("Error fetching PLAs:", error);
      setAssignments([]);
    } finally {
      setIsLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    void fetchPlas();
  }, [fetchPlas]);

  useEffect(() => {
    if (assignments.length === 0) return;

    setLessonSelections(previous => {
      const nextSelections = { ...previous };
      assignments.forEach(assignment => {
        if (!nextSelections[assignment.uuid]) {
          nextSelections[assignment.uuid] = assignment.defaultSelectedLessonKeys || [];
        }
      });
      return nextSelections;
    });
  }, [assignments]);

  const handleLessonSave = (plaUuid: string, lessonKeys: string[]) => {
    setHasEditedLessons(true);
    setLessonSelections(previous => ({ ...previous, [plaUuid]: lessonKeys }));
  };

  const handleLessonSaveAndInclude = (plaUuid: string, lessonKeys: string[]) => {
    setHasEditedLessons(true);
    setLessonSelections(previous => ({ ...previous, [plaUuid]: lessonKeys }));
    const assignment = assignments.find(item => item.uuid === plaUuid);
    if (assignment) {
      handleSelectionChange(previousSelection =>
        previousSelection.some(item => item.uuid === plaUuid)
          ? previousSelection
          : [...previousSelection, assignment]
      );
    }
  };

  const handleSettingsChange = (nextSettings: TrainingAssignmentSettingsValues) => {
    setSettings(nextSettings);
    setValidationErrors(previous => ({
      ...previous,
      settings: getSettingsValidation(nextSettings),
    }));
  };

  const handleSelectionChange = (
    selectionOrUpdater:
      | TrainingPlaAssignment[]
      | ((previous: TrainingPlaAssignment[]) => TrainingPlaAssignment[])
  ) => {
    const nextSelection =
      typeof selectionOrUpdater === "function"
        ? selectionOrUpdater(selectedAssignmentsRef.current)
        : selectionOrUpdater;

    setSelectedAssignments(nextSelection);
    setValidationErrors(previous => ({
      ...previous,
      assignments: getAssignmentsValidation(nextSelection),
    }));
  };

  const handleStepChange = (nextStep: number) => {
    if (currentStep === 0 && nextStep > currentStep) {
      setValidationErrors(previous => ({
        ...previous,
        assignments: getAssignmentsValidation(selectedAssignments),
      }));
      setShowStepStatus(true);
    }

    if (nextStep === TOTAL_STEPS - 1) {
      setShowStepStatus(true);
      setShowValidationMessages(true);
    }

    setCurrentStep(nextStep);
  };

  const isFormValid = useMemo(
    () =>
      !validationErrors.assignments.hasError &&
      !validationErrors.settings.description.hasError &&
      !validationErrors.settings.startDate.hasError &&
      !validationErrors.settings.timeframe.hasError,
    [validationErrors]
  );

  const resetFormState = () => {
    setCurrentStep(0);
    setShowStepStatus(false);
    setShowValidationMessages(false);
    setSelectedAssignments([]);
    setLessonSelections({});
    setHasEditedLessons(false);
    setSettings(getDefaultSettings());
    setValidationErrors(getInitialValidationErrors());
  };

  const handleSubmit = async () => {
    if (!tenant || !isFormValid || selectedAssignments.length === 0 || !settings.startDate) return;

    setIsSubmitting(true);
    try {
      const now = new Date();
      const approvedAt = now.toISOString();
      const selectedStart = normalizeToMidnight(settings.startDate);
      const normalizedNow = normalizeToMidnight(now);
      const isFutureStart = selectedStart.getTime() > normalizedNow.getTime();

      const results = await Promise.allSettled(
        selectedAssignments.map(assignment => {
          const selectedLessonKeys = lessonSelections[assignment.uuid] || [];
          return getPlaClient().updatePLA(tenant, assignment.uuid, {
            lessons: selectedLessonKeys,
            approvedAt,
            endDate: settings.endDate || undefined,
            name: settings.name,
            description: settings.description,
            ...(isFutureStart ? { startDate: selectedStart.toISOString() } : {}),
          });
        })
      );

      const failedUuids: string[] = [];
      results.forEach((result, index) => {
        if (result.status === "rejected" || result.value?.error) {
          failedUuids.push(selectedAssignments[index]?.uuid || "");
        }
      });

      const successCount = selectedAssignments.length - failedUuids.length;

      if (successCount > 0) {
        try {
          const { data, error } = await getPlaClient().getTenantPLAs(tenant);
          if (error) throw error;

          const refreshedAssignments = transformPlaData(data?.assignments);
          setAssignments(refreshedAssignments);
          setSelectedAssignments(
            refreshedAssignments.filter(assignment => failedUuids.includes(assignment.uuid))
          );
        } catch (error) {
          console.error("Error refreshing assignments after launch:", error);
          showToast({
            severity: "warn",
            summary: "Assignments launched",
            detail: `${successCount} personalized assignment${successCount !== 1 ? "s were" : " was"} launched, but the page could not be refreshed. Please reload to see the latest status.`,
          });
          return;
        }
      }

      if (failedUuids.length === 0) {
        resetFormState();
        showToast({
          severity: "success",
          summary: "Assignments launched",
          detail: `${successCount} personalized assignments were launched.`,
        });
        return;
      }

      setCurrentStep(TOTAL_STEPS - 1);
      setShowStepStatus(true);
      setShowValidationMessages(true);

      showToast({
        severity: successCount > 0 ? "warn" : "error",
        summary: successCount > 0 ? "Some assignments failed" : "Assignments failed",
        detail:
          successCount > 0
            ? `${successCount} personalized assignments were launched. ${failedUuids.length} failed.`
            : "No personalized assignments were launched.",
      });
    } catch (error) {
      console.error("Error submitting training plan:", error);
      showToast({
        severity: "error",
        summary: "Assignments failed",
        detail: "Unable to launch personalized assignments.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // `lessonSelections` is auto-populated from each PLA's recommended lessons on
  // load, so it is non-empty even when the user hasn't touched anything. Track an
  // explicit "user edited a lesson set" flag instead of treating any populated
  // selection as unsaved work.
  const isDirty = useMemo(
    () =>
      currentStep !== 0 ||
      selectedAssignments.length > 0 ||
      hasEditedLessons ||
      JSON.stringify(settings) !== JSON.stringify(getDefaultSettings()),
    [currentStep, hasEditedLessons, selectedAssignments.length, settings]
  );

  const blocker = useBlocker(isDirty && !isSubmitting);

  const steps = [
    {
      header: showStepStatus
        ? `Available Assignments ${!validationErrors.assignments.hasError ? " ✓" : " ✗"}`
        : "Available Assignments",
      title: "Available Assignments",
      content: (
        <TrainingAvailableAssignments
          assignments={assignments}
          selectedAssignments={selectedAssignments}
          onSelectionChange={handleSelectionChange}
          isLoading={isLoading}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.assignments}
          lessonSelections={lessonSelections}
          onLessonSave={handleLessonSave}
          onLessonSaveAndInclude={handleLessonSaveAndInclude}
        />
      ),
    },
    {
      header: showStepStatus
        ? `Assignment Settings ${
            !(
              validationErrors.settings.description.hasError ||
              validationErrors.settings.timeframe.hasError ||
              validationErrors.settings.startDate.hasError
            )
              ? " ✓"
              : " ✗"
          }`
        : "Assignment Settings",
      title: "Assignment Settings",
      content: (
        <TrainingAssignmentSettings
          settings={settings}
          onSettingsChange={handleSettingsChange}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.settings}
        />
      ),
    },
    {
      header: "Review",
      title: "Review",
      content: (
        <TrainingReview
          selectedAssignments={selectedAssignments}
          settings={settings}
          onStepChange={handleStepChange}
          validationErrors={validationErrors}
          isFormValid={isFormValid}
        />
      ),
    },
  ];

  const createPlaButton =
    createPlaData && isSjAdmin ? (
      <div className="flex justify-end">
        <Button label="Create PLA Data" icon="pi pi-plus" onClick={() => setCreatePlaOpen(true)} />
      </div>
    ) : null;

  return (
    <div className="flex min-h-[60vh] flex-col">
      <CreatePlaDialog
        visible={createPlaOpen}
        onHide={() => setCreatePlaOpen(false)}
        tenant={tenant}
        cweDescriptions={cweDescriptions}
        onCreated={fetchPlas}
      />

      <div className="flex flex-1 flex-col">
        <FormStepper
          steps={steps}
          currentStep={currentStep}
          setCurrentStep={handleStepChange}
          handleSubmit={() => void handleSubmit()}
          isNew
          isSubmitting={isSubmitting}
          submitButtonText="Launch Assignments"
          submitDisabled={!isFormValid || isSubmitting}
          afterStepperSlot={createPlaButton}
        />
      </div>

      <Dialog
        header="Unsaved Changes"
        visible={blocker.state === "blocked"}
        onHide={() => blocker.reset?.()}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Stay" severity="secondary" onClick={() => blocker.reset?.()} />
            <Button label="Leave" severity="danger" onClick={() => blocker.proceed?.()} />
          </div>
        }
      >
        <p>You have unsaved changes. Are you sure you want to leave?</p>
      </Dialog>
    </div>
  );
}
